/**
 * The TextEditor container
 */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Container } from "reactstrap";
import Page from "../../components/Page";
import TextEditorForm from "../../components/TextEditorForm";
import CMSBlock from "../../components/CMSBlock";
import { getFormValues } from "redux-form";
import EditorFormHelpers from "../../components/TextEditorForm/utils/TextEditorFormHelpers";
import { COLORS, COLORTYPES } from "../../utils/colors";
import { list as listFonts } from "../../redux/fonts";
import fontSelectors from "../../redux/fonts/selectors";
import { loadTextEditor } from "../../redux/xapis";

import cmsBlocksSelectors from "../../redux/cmsblocks/selectors";
import filter from "lodash/filter";
import { useSsrEffect } from "@issr/core";

const _ = {
  filter: filter,
};

/**
 * TextEditor container, the landing page for logged in users
 */
const TextEditor = (props, context) => {
  const {
    currentFormState,
    language,
    textEditorCMSBlocks,
    fonts,
    size,
    loadedFontList,
    loadedTextEditor,
    loadingTextEditor,
  } = useSelector((state) => ({
    currentFormState: EditorFormHelpers.extractFormState(
      getFormValues("editorForm")(state)
    ),
    fonts: fontSelectors.selectFontsForForm(state.fonts.list),
    size: state.editorSize,
    loadingFontList: state.fonts.loadingList,
    loadedFontList: state.fonts.loadedList,
    textEditor: state.xapis.textEditor,
    loadingTextEditor: state.xapis.loadingTextEditor,
    loadedTextEditor: state.xapis.loadedTextEditor,
    textEditorCMSBlocks: cmsBlocksSelectors.selectCMSBlocksForPage(
      state,
      state.xapis.textEditorCMSBlocksSlugs
    ),
    language: state.i18nState.lang,
  }));

  const initialColor = COLORS[COLORTYPES.GLOSSY][0];
  const initialFont = {
    id: 96,
    title: "Roboto",
    thickness: 8,
    width: 3,
    slant: 1,
    min_font_size: 13,
    created_at: "2019-09-18T08:20:41.000Z",
    updated_at: "2019-09-18T08:20:41.000Z",
    preview: [
      {
        size: "200w",
        url: "https://content.myfolie.de/cache/fonts/96/200w.jpg",
        webp_url: "https://content.myfolie.de/cache/fonts/96/200w.webp",
        width: 200,
        height: 20,
      },
    ],
    styles: [
      { title: "regular", available: true, loaded: false },
      { title: "bold", available: true, loaded: false },
      { title: "italic", available: true, loaded: false },
      { title: "italic_bold", available: true, loaded: false },
    ],
  };

  const dataLayer = [
    {
      event: "view_item",
      value: 50,
      items: [
        { id: "text", google_business_vertical: "retail" },
        { id: "adhesive-letters", google_business_vertical: "retail" },
        {
          id: "customised-text-as-a-wall-sticker",
          google_business_vertical: "retail",
        },
        {
          id: "text-of-your-choice-as-wall-tattoo",
          google_business_vertical: "retail",
        },
        { id: "Klebeschrift nach Wunsch", google_business_vertical: "retail" },
        {
          id: "Klebefolie selbst gestalten",
          google_business_vertical: "retail",
        },
        {
          id: "Fahrzeugbeschriftung mit individuellem Text",
          google_business_vertical: "retail",
        },
        {
          id: "Beschriftung aus Klebefolie",
          google_business_vertical: "retail",
        },
      ],
    },
    {
      ecommerce: {
        detail: {
          actionField: {
            list: "LandingPage",
          },
          products: [
            {
              name: "Klebebuchstaben - Schriftart, Farbe und Größe frei wählbar",
              id: "adhesive-letters",
              price: "4.95",
              category:
                "Für dein Gewerbe|Für dein Fahrzeug|Nach Gewerbe|Für Restaurants|Für Cafés|Für Imbisse|Für dein Büro|Nach Einsatzzweck|\u00d6ffnungszeiten|Für Friseure|Für Arztpraxen|Wegweiser|Raumbeschriftung|Für dein Schaufenster|Nach Motiv|Für Ladengeschäfte",
            },
            {
              name: "Klebeschrift nach Wunsch",
              id: "Klebeschrift nach Wunsch",
              price: "4.95",
              category:
                "Für dein Gewerbe|Für dein Fahrzeug|Nach Gewerbe|Für Restaurants|Für Cafés|Für Imbisse|Für dein Büro|Nach Einsatzzweck|\u00d6ffnungszeiten|Für Friseure|Für Arztpraxen|Wegweiser|Raumbeschriftung|Für dein Schaufenster|Nach Motiv|Für Ladengeschäfte",
            },
            {
              name: "Klebefolie selbst gestalten",
              id: "Klebefolie selbst gestalten",
              price: "4.95",
              category:
                "Für dein Gewerbe|Für dein Fahrzeug|Nach Gewerbe|Für Restaurants|Für Cafés|Für Imbisse|Für dein Büro|Nach Einsatzzweck|\u00d6ffnungszeiten|Für Friseure|Für Arztpraxen|Wegweiser|Raumbeschriftung|Für dein Schaufenster|Nach Motiv|Für Ladengeschäfte",
            },
            {
              name: "Fahrzeugbeschriftung mit individuellem Text",
              id: "Fahrzeugbeschriftung mit individuellem Text",
              price: "4.95",
              category:
                "Für dein Gewerbe|Für dein Fahrzeug|Nach Gewerbe|Für Restaurants|Für Cafés|Für Imbisse|Für dein Büro|Nach Einsatzzweck|\u00d6ffnungszeiten|Für Friseure|Für Arztpraxen|Wegweiser|Raumbeschriftung|Für dein Schaufenster|Nach Motiv|Für Ladengeschäfte",
            },
            {
              name: "Beschriftung aus Klebefolie",
              id: "Beschriftung aus Klebefolie",
              price: "4.95",
              category:
                "Für dein Gewerbe|Für dein Fahrzeug|Nach Gewerbe|Für Restaurants|Für Cafés|Für Imbisse|Für dein Büro|Nach Einsatzzweck|\u00d6ffnungszeiten|Für Friseure|Für Arztpraxen|Wegweiser|Raumbeschriftung|Für dein Schaufenster|Nach Motiv|Für Ladengeschäfte",
            },
            {
              name: "Beschriftung aus Klebefolie",
              id: "text",
              price: "4.95",
              category:
                "Für dein Gewerbe|Für dein Fahrzeug|Nach Gewerbe|Für Restaurants|Für Cafés|Für Imbisse|Für dein Büro|Nach Einsatzzweck|\u00d6ffnungszeiten|Für Friseure|Für Arztpraxen|Wegweiser|Raumbeschriftung|Für dein Schaufenster|Nach Motiv|Für Ladengeschäfte",
            },
          ],
        },
      },
    },
  ];

  const dispatch = useDispatch();
  /** frontload async calls */
  const frontload = async () => {
    if (!loadedFontList) {
      dispatch(listFonts());
    }
    if (!loadedTextEditor && !loadingTextEditor) {
      dispatch(loadTextEditor());
    }
  };

  useSsrEffect(frontload, [language]);
  const presetFromUrl = new URLSearchParams(props.location.search); // editorType = product

  const text_editor_intro = _.filter(textEditorCMSBlocks, function (cmsBlock) {
    return cmsBlock.slug === "text_editor_intro";
  });
  const text_editor_block_1 = _.filter(
    textEditorCMSBlocks,
    function (cmsBlock) {
      return cmsBlock.slug === "text_editor_block_1";
    }
  );
  const text_editor_block_2 = _.filter(
    textEditorCMSBlocks,
    function (cmsBlock) {
      return cmsBlock.slug === "text_editor_block_2";
    }
  );
  const text_editor_faqs = _.filter(textEditorCMSBlocks, function (cmsBlock) {
    return cmsBlock.slug === "text_editor_faqs";
  });
  return (
    <Page
      id="texteditor"
      title={context.t("Design and Customize Adhesive Lettering online")}
      lang={language}
      pageType="texteditor"
      dataLayer={dataLayer}
      description={context.t(
        "Easily create your custom adhesive letters in multiple colors and many great fonts"
      )}
      image="https://cdn.myfolie.com/media/wysiwyg/foto-klebeschrift.jpg"
    >
      {fonts && (
        <TextEditorForm
          currentFormState={currentFormState}
          initialValues={{
            text: presetFromUrl.get("text") || "", //context.t("Your Text"),
            materialSerie: presetFromUrl.get("materialSerie") || 1,
            baseDimension: { type: "width", size: size.width },
            calculatedDimension: { type: "height", size: size.height },
            lines: [
              {
                font: { value: initialFont, label: initialFont.title },
                allignment: "start",
                fontSize: 60,
                lineHeight: 1,
                text: presetFromUrl.get("text") || "", //context.t("Your Text"),
                color: { value: initialColor, label: initialColor.title },
                computedTextLength: 0,
                computedLineHeight: 0,
                computedLineWidth: 0,
              },
            ],
            amount: presetFromUrl.get("amount") || 1,
          }}
          language={language}
          previewContent={
            <Container className="my-3">
              {text_editor_intro[0] && (
                <CMSBlock cmsBlock={text_editor_intro[0]} />
              )}
            </Container>
          }
        />
      )}

      {text_editor_block_1[0] && (
        <Container className="my-5">
          <CMSBlock cmsBlock={text_editor_block_1[0]} />
        </Container>
      )}
      {text_editor_block_2[0] && (
        <Container className="my-5">
          <CMSBlock cmsBlock={text_editor_block_2[0]} />
        </Container>
      )}
      {text_editor_faqs[0] && (
        <Container className="my-5">
          <CMSBlock cmsBlock={text_editor_faqs[0]} />
        </Container>
      )}
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
TextEditor.contextTypes = {
  t: PropTypes.func,
};

/** export the component as redux connected component */
export default TextEditor;
